import React from "react";
import PropTypes from "prop-types";
import A4Page from "./A4Page";
import blockRegistry from "../blocks/blockRegistry";

/**
 * ManifestPage - renders one resolved manifest Page (boardreport.Page, from
 * GET /board-reports/{id}/runs/{runId}'s `manifest.pages`) as an A4Page whose
 * children are the page's blocks, resolved via blockRegistry.
 *
 * Every block receives the same prop bundle - `title`/`binding`/`period` plus
 * `boardReportId`/`runId` (so figure blocks can build their own endpoint URLs
 * via useBlockData), `narrative` (so page_summary/exec_narrative/decision can
 * read the run's narrative object), and `drivers` (so driver_table can render
 * the run's frozen driver annotations). Blocks destructure only the props they
 * need, so handing the full bundle to each is harmless and keeps the wiring
 * uniform rather than special-casing per type.
 *
 * SP6 seam: `metrics`/`metricsLoading` (from useReportMetrics.js, via BoardReportPages) are
 * likewise forwarded to every block so the narrative blocks can resolve `{{fig:KEY}}` @figure
 * tokens (renderNarrative.jsx) and gate their own loading state on `metricsLoading`. Both are
 * optional and undefined/false by default, so a caller that never passes them (every pre-SP6
 * caller) renders exactly as before.
 *
 * SP6 seam: `selectable`/`selectedBlockId`/`onSelectBlock` add an OPTIONAL block-selection
 * wrapper around each block, for the report-canvas + contextual-inspector editor (preview-only).
 * When `selectable` is truthy, each block's `<Component>` is wrapped in a keyboard-operable
 * `role="button"` div (`.a4-selectable`, `.is-selected` when `block.id === selectedBlockId`)
 * that calls `onSelectBlock(block.id, block.type, block.binding)` on click/Enter/Space - passing
 * `block.binding` as the 3rd arg so the container needn't reload the manifest just to know what a
 * selected block is bound to. When `selectable` is falsy/absent (every pre-SP6 caller - the print
 * path via BoardReportPrint->BoardReportPages, and the SP4 standalone preview) each block renders
 * BARE, exactly as before, so the PDF and the SP4 preview stay byte-identical.
 *
 * The page's own title (not the report title, which CoverPage already carries)
 * is used as A4Page's running-header title, so each interior page identifies
 * its own section (e.g. "Financial Performance") alongside the period.
 */
const ManifestPage = ({
  page,
  period,
  pageNumber,
  totalPages,
  boardReportId,
  runId,
  narrative,
  drivers,
  metrics,
  metricsLoading,
  selectable,
  selectedBlockId,
  selectedBinding,
  onSelectBlock,
}) => {
  const blocks = page?.blocks ?? [];

  return (
    <A4Page title={page?.title} period={period} pageNumber={pageNumber} totalPages={totalPages}>
      {blocks.map((block) => {
        const Component = blockRegistry.get(block.type);

        if (!Component) {
          return (
            <div
              key={block.id}
              className="a4-block a4-unknown-block"
              data-widget-loading="false"
            >
              Unknown block type: {block.type}
            </div>
          );
        }

        // driver_table gets per-row selection on the editable canvas: clicking a driver row opens
        // just that driver in the inspector (onSelectBlock with a driverIndex-augmented binding),
        // and the currently-open row is highlighted. Only wired when `selectable` — the print path
        // and non-selectable preview pass neither prop, so DriverTableBlock renders byte-identical.
        const driverSelectionProps =
          selectable && block.type === "driver_table"
            ? {
                onSelectDriver: (driverIndex) =>
                  onSelectBlock &&
                  onSelectBlock(block.id, block.type, { ...(block.binding || {}), driverIndex }),
                selectedDriverIndex:
                  block.id === selectedBlockId && selectedBinding && selectedBinding.driverIndex != null
                    ? selectedBinding.driverIndex
                    : null,
              }
            : {};

        const element = (
          <Component
            title={block.title}
            binding={block.binding}
            period={period}
            boardReportId={boardReportId}
            runId={runId}
            narrative={narrative}
            drivers={drivers}
            metrics={metrics}
            metricsLoading={metricsLoading}
            {...driverSelectionProps}
          />
        );

        if (!selectable) {
          return React.cloneElement(element, { key: block.id });
        }

        const isSelected = block.id === selectedBlockId;
        return (
          <div
            key={block.id}
            className={"a4-selectable" + (isSelected ? " is-selected" : "")}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={() => onSelectBlock && onSelectBlock(block.id, block.type, block.binding)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectBlock && onSelectBlock(block.id, block.type, block.binding);
              }
            }}
          >
            {element}
          </div>
        );
      })}
    </A4Page>
  );
};

ManifestPage.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    blocks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        title: PropTypes.string,
        binding: PropTypes.object,
        optional: PropTypes.bool,
      })
    ),
  }).isRequired,
  period: PropTypes.string,
  pageNumber: PropTypes.number,
  totalPages: PropTypes.number,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  narrative: PropTypes.object,
  drivers: PropTypes.arrayOf(PropTypes.object),
  metrics: PropTypes.object,
  metricsLoading: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedBlockId: PropTypes.string,
  // The selected block's binding (incl. an optional driverIndex for driver-row selection) - used to
  // highlight the open driver row on a selected driver_table.
  selectedBinding: PropTypes.object,
  onSelectBlock: PropTypes.func,
};

export default ManifestPage;
